import React from 'react';
import { Card, CardContent, Typography, useTheme } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { formatarPercentual, TRACO } from '../utils/series';

const MOEDA = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 0
});

/**
 * O eixo em português, e curto — mas nunca mentindo sobre a própria grade.
 *
 * "140000" obriga o leitor a contar casas; "140 mil" ele lê de relance. O eixo
 * é referência, não é a fonte do valor exato — esse mora no tooltip e na lista.
 *
 * O ARREDONDAMENTO ERA CEGO E ERRAVA 14% NO PRIMEIRO RÓTULO. Com
 * `maximumFractionDigits: 0` a grade de 3.500 / 7.000 / 10.500 / 14.000 saía
 * escrita "4 mil / 7 mil / 11 mil / 14 mil": o traço horizontal do gráfico
 * estava em 3.500 e o rótulo dele dizia 4 mil. Numa escala pequena, uma casa
 * decimal é a diferença entre referência e invenção; numa escala grande, "140,0
 * mil" é ruído. Por isso a casa decimal entra só quando ela muda a leitura.
 */
const formatarEixo = (valor) => {
  const n = Number(valor);
  if (!Number.isFinite(n)) return '';
  const absoluto = Math.abs(n);
  if (absoluto >= 1000000) {
    return `${(n / 1000000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
  }
  if (absoluto >= 1000) {
    // Abaixo de cem mil o passo da grade costuma ser fracionário em milhares
    // (3.500, 10.500). Acima disso ele é redondo e a casa decimal só polui.
    const casas = absoluto < 100000 ? 1 : 0;
    return `${(n / 1000).toLocaleString('pt-BR', { maximumFractionDigits: casas })} mil`;
  }
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
};

const LineChartCard = ({ title, data, lines, height = 300 }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const ausente = (valor) => valor === null || valor === undefined || Number.isNaN(valor);

  const formatCurrency = (value) => (ausente(value) ? TRACO : MOEDA.format(value));

  const formatPercentage = (value) => (ausente(value) ? TRACO : formatarPercentual(value, 2));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          // O balão herda a linguagem de vidro do resto do produto: fundo
          // translúcido, desfoque e o filete claro do topo. Um retângulo opaco
          // de canto reto, aqui, é a peça que denuncia que o gráfico veio de
          // outra biblioteca.
          backgroundColor: isDark ? 'rgba(16, 19, 25, 0.82)' : 'rgba(255, 255, 255, 0.86)',
          backdropFilter: 'blur(16px) saturate(1.5)',
          WebkitBackdropFilter: 'blur(16px) saturate(1.5)',
          padding: '10px 12px',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '12px',
          boxShadow: isDark
            ? 'inset 0 1px 0 rgba(255,255,255,0.14), 0 14px 34px -16px rgba(0,0,0,0.85)'
            : 'inset 0 1px 0 rgba(255,255,255,0.9), 0 12px 28px -16px rgba(15,23,34,0.4)',
          color: theme.palette.text.primary,
          fontVariantNumeric: 'tabular-nums'
        }}>
          <p style={{ margin: 0, fontWeight: 600, letterSpacing: '0.01em' }}>{label}</p>
          {payload.map((entry, index) => {
            // O formato vem declarado na própria série. Antes era adivinhado
            // pelo nome da chave, e "margem" virava R$ 30,61 em vez de 30,61%.
            const serie = (lines || []).find((l) => l.dataKey === entry.dataKey);
            const ehPercentual =
              serie?.format === 'percentage' ||
              entry.dataKey.includes('percent') ||
              entry.dataKey.includes('%');
            return (
            <p key={index} style={{ margin: '5px 0', color: entry.color }}>
              {entry.name}: {ehPercentual ? formatPercentage(entry.value) : formatCurrency(entry.value)}
            </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h5" component="h3" sx={{ mb: 2.5 }}>
          {title}
        </Typography>
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            {/* SÓ AS HORIZONTAIS. A grade cruzada desenha uma gaiola atrás de
                três pontos de dado; as verticais não ajudam a ler valor nenhum
                — quem marca o mês é o rótulo do eixo. E ela é contínua, não
                tracejada: tracejo tem peso visual de conteúdo, e grade é
                referência. */}
            <CartesianGrid
              vertical={false}
              stroke={isDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,34,0.08)'}
            />
            <XAxis
              dataKey="month"
              stroke="transparent"
              tickLine={false}
              tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
              dy={8}
            />
            <YAxis
              tickFormatter={formatarEixo}
              stroke="transparent"
              tickLine={false}
              width={62}
              tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            {/* O TEXTO DA LEGENDA NÃO CARREGA A COR DA SÉRIE.
                O recharts pinta o rótulo com o `stroke` da linha, e cor de
                traçado é escolhida para se distinguir de outra cor — não para
                se ler sobre o fundo. Medido: "Margem Líquida" saiu a 2,59:1 no
                tema claro, abaixo do mínimo de 4,5:1 para texto. Quem identifica
                a série é o quadradinho colorido ao lado; o nome dela é texto, e
                texto usa cor de texto. */}
            <Legend
              wrapperStyle={{ color: theme.palette.text.secondary }}
              formatter={(valor) => (
                <span style={{ color: theme.palette.text.secondary }}>{valor}</span>
              )}
            />
            {lines.map((line, index) => (
              <Line
                key={index}
                type="monotone"
                dataKey={line.dataKey}
                stroke={line.color}
                name={line.name}
                strokeWidth={2.25}
                // Ponto vazado com o miolo da cor do fundo: sobre vidro escuro,
                // bolinha cheia engorda a linha e some o valor; vazada, ela lê
                // como marcador de medição.
                dot={{ r: 3.5, strokeWidth: 2, fill: isDark ? '#0A0C11' : '#ffffff' }}
                activeDot={{ r: 6, strokeWidth: 2, fill: isDark ? '#0A0C11' : '#ffffff' }}
                // A LINHA NÃO SE ANIMA, e isso foi medido. O recharts reinicia o
                // desenho a cada mudança de layout — troca de aba, giro do
                // aparelho, redimensionamento —, e não só na primeira montagem.
                // Numa captura de página inteira as duas séries saíram como
                // cotocos de 20px, que é o que qualquer leitor veria a cada
                // reflow. Gráfico financeiro que se redesenha sozinho parece
                // dado chegando; aqui o dado já chegou.
                isAnimationActive={false}
                // Mês sem lançamento interrompe a linha. Ligar fevereiro direto
                // a junho faria a inclinação mentir sobre a velocidade da
                // mudança — e inventar número na camada visual.
                connectNulls={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default LineChartCard;
